import type { DCEL, Polygon } from "./dcel";
import { oppositeHalfEdgeIndex } from "./dcelFromPolygons";
import { pointInPolygon } from "./pointInPolygon";

type SetOpEmitter<T> = (dcel: DCEL, halfEdgeLoops: number[][]) => T[];

export function setOpByPredicate<T>(
  dcel: DCEL,
  predicate: (halfEdgeIndex: number) => boolean,
  emitter: SetOpEmitter<T>,
): T[] {
  return emitter(dcel, traverseEdges(dcel, predicate));
}

export function setOpByPredicatePolygons(dcel: DCEL, predicate: (halfEdgeIndex: number) => boolean): Polygon[] {
  return setOpByPredicate(dcel, predicate, halfEdgeLoopsToPolygons);
}

export function union<T>(dcel: DCEL, polygonIds: number[], emitter: SetOpEmitter<T>): T[] {
  return setOpByPredicate(
    dcel,
    (halfEdgeIndex) => {
      const face = dcel.halfEdgeToFace[halfEdgeIndex];
      const polygons = dcel.faceToPolygons[face];

      const opp = oppositeHalfEdgeIndex(halfEdgeIndex);
      const oppFace = dcel.halfEdgeToFace[opp];
      const oppPolygons = dcel.faceToPolygons[oppFace];

      return setContains(polygons, polygonIds) && !setContains(oppPolygons, polygonIds);
    },
    emitter,
  );
}

export function subtract<T>(
  dcel: DCEL,
  minuendPolygonIds: number[],
  subtrahendPolygonIds: number[],
  emitter: SetOpEmitter<T>,
): T[] {
  // subtract requires two traversals

  // first, find all of the faces that are in the result
  const loops = traverseEdges(dcel, (halfEdgeIndex) => {
    const face = dcel.halfEdgeToFace[halfEdgeIndex];
    const polygons = dcel.faceToPolygons[face];

    return setContains(polygons, minuendPolygonIds) && !setContains(polygons, subtrahendPolygonIds);
  });

  // keep track of all of the faces in the result
  const faces: { [faceId: number]: boolean } = {};
  for (const loop of loops) {
    for (const halfEdgeIndex of loop) {
      const faceId = dcel.halfEdgeToFace[halfEdgeIndex];
      faces[faceId] = true;
    }
  }

  // for all of those faces, find the edges whose parent face
  // are in the first traversal, but whose opposite edge is not.
  // this way, we prevent having any unnecessary edges in the
  // result
  return setOpByPredicate(
    dcel,
    (halfEdgeIndex) => {
      const face = dcel.halfEdgeToFace[halfEdgeIndex];

      const opp = oppositeHalfEdgeIndex(halfEdgeIndex);
      const oppFace = dcel.halfEdgeToFace[opp];

      return faces[face] && !faces[oppFace];
    },
    emitter,
  );
}

export function intersect<T>(dcel: DCEL, polygonIds: number[], emitter: SetOpEmitter<T>): T[] {
  return setOpByPredicate(
    dcel,
    (halfEdgeIndex) => {
      const face = dcel.halfEdgeToFace[halfEdgeIndex];
      const polygons = dcel.faceToPolygons[face];

      return setContainsAll(polygons, polygonIds);
    },
    emitter,
  );
}

/** Intersects one polygon with the union of one or more other polygons. */
export function intersectUnion<T>(
  dcel: DCEL,
  polygonId: number,
  unionPolygonIds: number[],
  emitter: SetOpEmitter<T>,
): T[] {
  return setOpByPredicate(
    dcel,
    (halfEdgeIndex) => {
      const polygons = dcel.faceToPolygons[dcel.halfEdgeToFace[halfEdgeIndex]];
      return polygons.has(polygonId) && setContains(polygons, unionPolygonIds);
    },
    emitter,
  );
}

export function polyUnion(dcel: DCEL, polygonIds: number[]): Polygon[] {
  return union(dcel, polygonIds, halfEdgeLoopsToPolygons);
}

export function polySubtract(dcel: DCEL, minuendPolygonIds: number[], subtrahendPolygonIds: number[]): Polygon[] {
  return subtract(dcel, minuendPolygonIds, subtrahendPolygonIds, halfEdgeLoopsToPolygons);
}

export function polyIntersect(dcel: DCEL, polygonIds: number[]): Polygon[] {
  return intersect(dcel, polygonIds, halfEdgeLoopsToPolygons);
}

export function setContains(set: Set<number>, values: number[]): boolean {
  if (!set) {
    return false;
  }

  for (const value of values) {
    if (set.has(value)) {
      return true;
    }
  }
  return false;
}

export function setContainsAll(set: Set<number>, values: number[]): boolean {
  if (!set) {
    return false;
  }
  for (const value of values) {
    if (!set.has(value)) {
      return false;
    }
  }
  return true;
}

function traverseEdges(dcel: DCEL, predicate: (halfEdgeIndex: number) => boolean): number[][] {
  const visitedHalfEdges: { [index: number]: boolean } = {};

  const halfEdgeLoops: number[][] = [];

  for (let i = 0; i < dcel.halfEdges.length; i++) {
    if (visitedHalfEdges[i]) {
      continue;
    }

    let start;
    if (predicate(i)) {
      start = i;
    } else {
      continue;
    }

    const curHalfEdges: number[] = [];
    halfEdgeLoops.push(curHalfEdges);

    let curr = start;
    visitedHalfEdges[curr] = true;
    curHalfEdges.push(curr);

    while (true) {
      const next = nextHalfEdgeMatchingPredicate(dcel, curr, predicate);
      if (next === start || next < 0) {
        break;
      }

      if (visitedHalfEdges[next]) {
        throw new Error("infinite loop");
      }

      curr = next;
      visitedHalfEdges[curr] = true;
      curHalfEdges.push(curr);
    }
  }

  return halfEdgeLoops;
}

export function halfEdgeLoopsToPolygons(dcel: DCEL, halfEdgeLoops: number[][]): Polygon[] {
  // classify loops as interior or exterior loops

  // for each exterior loop, find the interior loop that contains it

  const loopAreas = halfEdgeLoops.map((loop) => {
    let area = 0;
    for (let i = 0; i < loop.length; i++) {
      const halfEdgeIndex = loop[i];
      const nextHalfEdgeIndex = loop[(i + 1) % loop.length];

      const [x1, y1] = dcel.vertices[dcel.halfEdges[halfEdgeIndex][0]];
      const [x2, y2] = dcel.vertices[dcel.halfEdges[nextHalfEdgeIndex][0]];

      area += (x1 + x2) * (y1 - y2);
    }

    return area;
  });

  const interiorLoopsSortedByArea = halfEdgeLoops
    .map((_, i) => i)
    .filter((x) => loopAreas[x] < 0)
    .sort((a, b) => loopAreas[b] - loopAreas[a]);

  // for each exterior face, determine if there is an interior face surrounding it

  const faceToParentFace: number[] = [];

  for (let j = 0; j < halfEdgeLoops.length; j++) {
    if (loopAreas[j] < 0) {
      continue;
    }

    const faceArea = Math.abs(loopAreas[j]);
    const firstVertex = dcel.vertices[dcel.halfEdges[halfEdgeLoops[j][0]][0]];

    for (const interiorFaceId of interiorLoopsSortedByArea) {
      // The 1e-3 accounts for the fact that an interior face
      // may be exactly the same size as the exterior face when the
      // exterior face shares half edges with the interior one. This is
      // hacky and we could detect it without relying on flaky floating point
      // comparisons by simply determining if the faces share a half edge.
      if (Math.abs(loopAreas[interiorFaceId]) < faceArea + 1e-3) {
        continue;
      }

      // this allocates a lot of memory unnecessarily. Instead, we should have a function
      // to determine containment by simply looking up the vertices of the face w/o
      // allocating.
      const polygon = dcel.buildPolygonLoopFromHalfEdges(halfEdgeLoops[interiorFaceId]);

      if (pointInPolygon(firstVertex, polygon)) {
        faceToParentFace[j] = interiorFaceId;
        break;
      }
    }
  }

  const faceToChildFaces: number[][] = [];
  for (let faceId = 0; faceId < halfEdgeLoops.length; faceId++) {
    faceToChildFaces[faceId] = [];
  }

  for (let childId = 0; childId < faceToParentFace.length; childId++) {
    if (faceToParentFace[childId] === undefined) {
      continue;
    }

    const parentId = faceToParentFace[childId];
    faceToChildFaces[parentId].push(childId);
  }

  const finalLoops: Polygon[] = [];

  for (let i = 0; i < halfEdgeLoops.length; i++) {
    if (faceToParentFace[i] !== undefined) {
      continue;
    }

    const polygon = dcel.buildPolygonLoopFromHalfEdges(halfEdgeLoops[i]);

    const childPolygons = faceToChildFaces[i].map((childId) =>
      dcel.buildPolygonLoopFromHalfEdges(halfEdgeLoops[childId]),
    );

    finalLoops.push([polygon, ...childPolygons]);
  }

  return finalLoops;
}

function nextHalfEdgeMatchingPredicate(dcel: DCEL, halfEdgeIndex: number, predicate: (idx: number) => boolean): number {
  const oppositeIndex = oppositeHalfEdgeIndex(halfEdgeIndex);
  const startVertex = dcel.halfEdges[oppositeIndex][0];
  const startVertexHalfEdges = dcel.vertexHalfEdges[startVertex];

  const vertexHalfEdgeIndex = dcel.halfEdgeVertexIndices[oppositeIndex] + 1;

  for (let i = 0; i < startVertexHalfEdges.length; i++) {
    const halfEdgeIndex = startVertexHalfEdges[(vertexHalfEdgeIndex + i) % startVertexHalfEdges.length];

    if (predicate(halfEdgeIndex)) {
      return halfEdgeIndex;
    }
  }

  return -1;
}
